import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  UserPlus, 
  Search, 
  Edit, 
  Trash2, 
  Shield, 
  ShieldOff, 
  Mail, 
  Phone,
  Calendar,
  UserCog,
  Lock,
  Unlock,
  Eye,
  MoreVertical,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Building2,
  Upload,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Clock,
  FileText,
  MessageSquare,
  Pencil,
  Activity
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAnalysts() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [analysts, setAnalysts] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAnalyst, setSelectedAnalyst] = useState(null);
  const [saving, setSaving] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [analystStats, setAnalystStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsStartDate, setStatsStartDate] = useState('');
  const [statsEndDate, setStatsEndDate] = useState('');
  
  // New analyst form
  const [newAnalyst, setNewAnalyst] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    phone_pro: '',
    whatsapp: '',
    job_title: '',
    profile_photo_url: '',
    availability_start: '09:00',
    availability_end: '18:00',
    availability_days: [1, 2, 3, 4, 5],
    commission_crypto: 0,
    commission_virement: 0,
  });
  
  // Edit form
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_pro: '',
    whatsapp: '',
    job_title: '',
    profile_photo_url: '',
    status: 'active',
    commission_crypto: 0,
    commission_virement: 0,
    availability: {
      lundi: { active: true, start: '09:00', end: '18:00' },
      mardi: { active: true, start: '09:00', end: '18:00' },
      mercredi: { active: true, start: '09:00', end: '18:00' },
      jeudi: { active: true, start: '09:00', end: '18:00' },
      vendredi: { active: true, start: '09:00', end: '18:00' },
      samedi: { active: false, start: '10:00', end: '14:00' },
      dimanche: { active: false, start: '', end: '' },
    }
  });

  useEffect(() => {
    fetchAnalysts();
  }, []);

  const fetchAnalysts = async () => {
    try {
      const [analystsRes, officesRes] = await Promise.all([
        axios.get(`${API}/analysts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/offices`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setAnalysts(analystsRes.data);
      setOffices(Array.isArray(officesRes.data) ? officesRes.data : []);
    } catch (error) {
      console.error('Error fetching analysts:', error);
      toast.error('Erreur lors du chargement des analystes');
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalystStats = async (analystId, startDate, endDate) => {
    setStatsLoading(true);
    try {
      let url = `${API}/analysts/${analystId}/stats`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length) url += '?' + params.join('&');
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setAnalystStats(res.data);
    } catch { toast.error('Erreur chargement stats'); }
    finally { setStatsLoading(false); }
  };

  const openInfoDialog = (analyst) => {
    setSelectedAnalyst(analyst);
    setStatsStartDate('');
    setStatsEndDate('');
    setAnalystStats(null);
    setInfoDialogOpen(true);
    fetchAnalystStats(analyst.id);
  };

  const handleCreateAnalyst = async () => {
    if (!newAnalyst.email || !newAnalyst.password || !newAnalyst.first_name || !newAnalyst.last_name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setSaving(true);
    try {
      const res = await axios.post(`${API}/auth/register`, {
        email: newAnalyst.email,
        password: newAnalyst.password,
        first_name: newAnalyst.first_name,
        last_name: newAnalyst.last_name,
        phone: newAnalyst.phone,
        role: 'analyst',
        office_id: newAnalyst.office_id || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save extra fields (phone_pro, whatsapp, job_title, photo, availability)
      const userId = res.data?.user_id || res.data?.id;
      if (userId) {
        const extraFields = {};
        if (newAnalyst.phone_pro) extraFields.phone_pro = newAnalyst.phone_pro;
        if (newAnalyst.whatsapp) extraFields.whatsapp = newAnalyst.whatsapp;
        if (newAnalyst.job_title) extraFields.job_title = newAnalyst.job_title;
        if (newAnalyst.profile_photo_url) extraFields.profile_photo_url = newAnalyst.profile_photo_url;
        extraFields.commission_crypto = parseFloat(newAnalyst.commission_crypto) || 0;
        extraFields.commission_virement = parseFloat(newAnalyst.commission_virement) || 0;
        if (Object.keys(extraFields).length > 0) {
          await axios.put(`${API}/users/${userId}`, extraFields, { headers: { Authorization: `Bearer ${token}` } });
        }
      }
      
      toast.success('Analyste créé avec succès');
      setDialogOpen(false);
      setNewAnalyst({ email: '', password: '', first_name: '', last_name: '', phone: '', phone_pro: '', whatsapp: '', job_title: '', profile_photo_url: '', commission_crypto: 0, commission_virement: 0 });
      fetchAnalysts();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditAnalyst = async () => {
    if (!selectedAnalyst) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/users/${selectedAnalyst.id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Analyste mis à jour');
      setEditDialogOpen(false);
      fetchAnalysts();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (analyst) => {
    const newStatus = analyst.status === 'active' ? 'blocked' : 'active';
    try {
      await axios.put(`${API}/users/${analyst.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(newStatus === 'active' ? 'Accès réactivé' : 'Accès bloqué');
      fetchAnalysts();
    } catch (error) {
      toast.error('Erreur lors de la modification du statut');
    }
  };

  const handleDeleteAnalyst = async () => {
    if (!selectedAnalyst) return;
    
    setSaving(true);
    try {
      await axios.delete(`${API}/users/${selectedAnalyst.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Analyste supprimé');
      setDeleteDialogOpen(false);
      setSelectedAnalyst(null);
      fetchAnalysts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setSaving(false);
    }
  };

  const [showResetPwdDialog, setShowResetPwdDialog] = useState(null);
  const [customPassword, setCustomPassword] = useState('');

  const handleResetPassword = async () => {
    if (!showResetPwdDialog) return;
    const pwd = customPassword.trim() || Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-4).toUpperCase();
    try {
      await axios.put(`${API}/users/${showResetPwdDialog.id}/reset-password`, { 
        new_password: pwd 
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Nouveau mot de passe: ${pwd}`, { duration: 15000 });
      setShowResetPwdDialog(null);
      setCustomPassword('');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const openEditDialog = (analyst) => {
    setSelectedAnalyst(analyst);
    const defaultAvail = {
      lundi: { active: true, start: '09:00', end: '18:00' },
      mardi: { active: true, start: '09:00', end: '18:00' },
      mercredi: { active: true, start: '09:00', end: '18:00' },
      jeudi: { active: true, start: '09:00', end: '18:00' },
      vendredi: { active: true, start: '09:00', end: '18:00' },
      samedi: { active: false, start: '10:00', end: '14:00' },
      dimanche: { active: false, start: '', end: '' },
    };
    setEditForm({
      first_name: analyst.first_name || '',
      last_name: analyst.last_name || '',
      email: analyst.email || '',
      phone: analyst.phone || '',
      phone_pro: analyst.phone_pro || '',
      whatsapp: analyst.whatsapp || '',
      job_title: analyst.job_title || '',
      profile_photo_url: analyst.profile_photo_url || '',
      status: analyst.status || 'active',
      commission_crypto: analyst.commission_crypto || 0,
      commission_virement: analyst.commission_virement || 0,
      availability: analyst.availability || defaultAvail,
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (analyst) => {
    setSelectedAnalyst(analyst);
    setDeleteDialogOpen(true);
  };

  const filteredAnalysts = analysts.filter(analyst => {
    const searchLower = searchTerm.toLowerCase();
    return (
      analyst.email?.toLowerCase().includes(searchLower) ||
      analyst.first_name?.toLowerCase().includes(searchLower) ||
      analyst.last_name?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-emerald-500/20 text-emerald-500"><CheckCircle2 className="w-3 h-3 mr-1" /> Actif</Badge>;
      case 'blocked':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Bloqué</Badge>;
      case 'pending':
        return <Badge className="bg-amber-500/20 text-amber-500"><AlertTriangle className="w-3 h-3 mr-1" /> En attente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <UserCog className="w-6 h-6" />
              Gestion des Analystes
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {analysts.length} analyste{analysts.length > 1 ? 's' : ''} enregistré{analysts.length > 1 ? 's' : ''}
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-analyst-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Nouvel Analyste
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto max-w-lg">
              <DialogHeader>
                <DialogTitle>Créer un nouvel analyste</DialogTitle>
                <DialogDescription>
                  Les analystes peuvent gérer les clients et les placements qui leur sont assignés.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom *</Label>
                    <Input
                      value={newAnalyst.first_name}
                      onChange={(e) => setNewAnalyst(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="Jean"
                      data-testid="new-analyst-firstname"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom *</Label>
                    <Input
                      value={newAnalyst.last_name}
                      onChange={(e) => setNewAnalyst(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Dupont"
                      data-testid="new-analyst-lastname"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newAnalyst.email}
                      onChange={(e) => setNewAnalyst(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="analyste@example.com"
                      data-testid="new-analyst-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe *</Label>
                    <Input
                      type="text"
                      value={newAnalyst.password}
                      onChange={(e) => setNewAnalyst(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="MotDePasse123"
                      className="font-mono"
                      data-testid="new-analyst-password"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Titre / Fonction</Label>
                  <Input
                    value={newAnalyst.job_title}
                    onChange={(e) => setNewAnalyst(prev => ({ ...prev, job_title: e.target.value }))}
                    placeholder="Conseiller Financier"
                    data-testid="new-analyst-job-title"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Tél. personnel</Label>
                    <Input value={newAnalyst.phone} onChange={(e) => setNewAnalyst(prev => ({ ...prev, phone: e.target.value }))} placeholder="+33 6 12 34 56 78" />
                  </div>
                  <div className="space-y-2">
                    <Label>Tél. professionnel</Label>
                    <Input value={newAnalyst.phone_pro} onChange={(e) => setNewAnalyst(prev => ({ ...prev, phone_pro: e.target.value }))} placeholder="+33 1 23 45 67 89" />
                  </div>
                  <div className="space-y-2">
                    <Label>WhatsApp</Label>
                    <Input value={newAnalyst.whatsapp} onChange={(e) => setNewAnalyst(prev => ({ ...prev, whatsapp: e.target.value }))} placeholder="+33 6 ..." />
                  </div>
                </div>
                {/* Photo */}
                <div className="space-y-2">
                  <Label>Photo de profil</Label>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 space-y-2">
                      <Input value={newAnalyst.profile_photo_url} onChange={(e) => setNewAnalyst(prev => ({ ...prev, profile_photo_url: e.target.value }))} placeholder="URL de la photo..." />
                      <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-sm transition-colors">
                        <Upload className="w-4 h-4" />
                        Uploader
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          e.target.value = '';
                          try {
                            const fd = new FormData();
                            fd.append('file', file);
                            const res = await axios.post(`${API}/upload-image`, fd, { headers: { Authorization: `Bearer ${token}` } });
                            setNewAnalyst(prev => ({ ...prev, profile_photo_url: res.data.url }));
                            toast.success('Photo uploadee');
                          } catch { toast.error('Erreur upload'); }
                        }} />
                      </label>
                    </div>
                    {newAnalyst.profile_photo_url && <img src={newAnalyst.profile_photo_url} alt="" className="w-14 h-14 rounded-full object-cover" />}
                  </div>
                </div>
                {/* Commissions */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Commission Virement (%)</Label>
                    <Input type="number" step="0.1" min="0" max="100" value={newAnalyst.commission_virement} onChange={(e) => setNewAnalyst(prev => ({ ...prev, commission_virement: e.target.value }))} placeholder="0" data-testid="new-analyst-commission-virement" />
                  </div>
                  <div className="space-y-2">
                    <Label>Commission Crypto (%)</Label>
                    <Input type="number" step="0.1" min="0" max="100" value={newAnalyst.commission_crypto} onChange={(e) => setNewAnalyst(prev => ({ ...prev, commission_crypto: e.target.value }))} placeholder="0" data-testid="new-analyst-commission-crypto" />
                  </div>
                </div>
                {/* Horaires */}
                <div className="space-y-2 p-3 rounded-lg border border-border/50 bg-muted/10">
                  <Label className="text-xs font-semibold">Horaires de disponibilite</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Debut</Label>
                      <Input type="time" value={newAnalyst.availability_start} onChange={(e) => setNewAnalyst(prev => ({ ...prev, availability_start: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Fin</Label>
                      <Input type="time" value={newAnalyst.availability_end} onChange={(e) => setNewAnalyst(prev => ({ ...prev, availability_end: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-1 flex-wrap mt-2">
                    {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map((day, i) => {
                      const dayVal = i === 6 ? 0 : i + 1;
                      const isSelected = (newAnalyst.availability_days || []).includes(dayVal);
                      return (
                        <button key={dayVal} type="button"
                          className={`px-2 py-1 rounded text-xs font-medium transition-colors ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}
                          onClick={() => setNewAnalyst(prev => ({
                            ...prev,
                            availability_days: isSelected
                              ? prev.availability_days.filter(d => d !== dayVal)
                              : [...(prev.availability_days || []), dayVal]
                          }))}
                        >{day}</button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleCreateAnalyst} disabled={saving} data-testid="submit-analyst-btn">
                  {saving ? 'Création...' : 'Créer l\'analyste'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-analysts"
          />
        </div>

        {/* Analysts Grid */}
        {filteredAnalysts.length === 0 ? (
          <Card className="bg-card/50">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium mb-2">
                {searchTerm ? 'Aucun résultat' : 'Aucun analyste'}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {searchTerm 
                  ? 'Essayez avec d\'autres termes de recherche'
                  : 'Créez votre premier analyste pour commencer'}
              </p>
              {!searchTerm && (
                <Button onClick={() => setDialogOpen(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Créer un analyste
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-card/80 border-border/30">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border/30 text-xs text-muted-foreground uppercase tracking-wider">
                      <th className="text-left py-3 px-4">Analyste</th>
                      <th className="text-left py-3 px-4">Email</th>
                      <th className="text-left py-3 px-4 hidden md:table-cell">Téléphone</th>
                      <th className="text-left py-3 px-4 hidden lg:table-cell">Bureau</th>
                      <th className="text-left py-3 px-4">Statut</th>
                      <th className="text-left py-3 px-4 hidden lg:table-cell">Commissions</th>
                      <th className="text-left py-3 px-4 hidden md:table-cell">Créé le</th>
                      <th className="text-right py-3 px-4 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAnalysts.map((analyst) => (
                      <tr key={analyst.id} className="border-b border-border/10 hover:bg-white/[0.02] transition-colors cursor-pointer" onClick={() => openInfoDialog(analyst)} data-testid={`analyst-row-${analyst.id}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                              {analyst.profile_photo_url ? (
                                <img src={analyst.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover" onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
                              ) : null}
                              <span className="text-sm font-semibold text-primary">{analyst.first_name?.[0]}{analyst.last_name?.[0]}</span>
                            </div>
                            <span className="font-semibold text-sm">{analyst.first_name} {analyst.last_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground truncate max-w-[200px]">{analyst.email}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">{analyst.phone || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell" onClick={e => e.stopPropagation()}>
                          <select className="h-7 px-2 rounded border border-input bg-background text-xs" value={analyst.office_id || 'none'} onChange={async (e) => { const val = e.target.value === 'none' ? null : e.target.value; try { await axios.put(`${API}/users/${analyst.id}`, { office_id: val }, { headers: { Authorization: `Bearer ${token}` } }); toast.success(val ? 'Bureau assigné' : 'Bureau retiré'); fetchAnalysts(); } catch { toast.error('Erreur'); } }} data-testid={`analyst-office-${analyst.id}`}>
                            <option value="none">—</option>
                            {offices.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                          </select>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(analyst.status)}</td>
                        <td className="py-3 px-4 text-xs text-muted-foreground hidden lg:table-cell">V:{analyst.commission_virement || 0}% C:{analyst.commission_crypto || 0}%</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground hidden md:table-cell">{new Date(analyst.created_at).toLocaleDateString('fr-FR')}</td>
                        <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`analyst-menu-${analyst.id}`}><MoreVertical className="w-4 h-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openInfoDialog(analyst)}><Activity className="w-4 h-4 mr-2" />Info & Stats</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openEditDialog(analyst)}><Edit className="w-4 h-4 mr-2" />Modifier</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleStatus(analyst)}>
                                {analyst.status === 'active' ? <><ShieldOff className="w-4 h-4 mr-2" />Bloquer</> : <><Shield className="w-4 h-4 mr-2" />Réactiver</>}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => { setShowResetPwdDialog(analyst); setCustomPassword(''); }}><Lock className="w-4 h-4 mr-2" />Réinitialiser MDP</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-destructive" onClick={() => openDeleteDialog(analyst)}><Trash2 className="w-4 h-4 mr-2" />Supprimer</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier l&apos;analyste</DialogTitle>
              <DialogDescription>
                {selectedAnalyst?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Photo de profil */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  {editForm.profile_photo_url ? (
                    <img src={editForm.profile_photo_url} alt="Photo" className="w-20 h-20 rounded-full object-cover border-2 border-primary" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center text-2xl font-bold text-muted-foreground">
                      {(editForm.first_name?.[0] || '?').toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => document.getElementById('analyst-photo-upload').click()}>
                    Changer la photo
                  </Button>
                  {editForm.profile_photo_url && (
                    <Button variant="outline" size="sm" className="text-xs text-red-500" onClick={() => setEditForm(prev => ({ ...prev, profile_photo_url: '' }))}>
                      Supprimer
                    </Button>
                  )}
                </div>
                <input
                  id="analyst-photo-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error('Photo trop lourde (max 5 Mo)'); return; }
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await axios.post(`${API}/upload-image`, formData, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      setEditForm(prev => ({ ...prev, profile_photo_url: res.data.url }));
                      toast.success('Photo mise à jour');
                    } catch { toast.error('Erreur upload photo'); }
                  }}
                  data-testid="analyst-photo-upload"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input value={editForm.first_name} onChange={(e) => setEditForm(prev => ({ ...prev, first_name: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input value={editForm.last_name} onChange={(e) => setEditForm(prev => ({ ...prev, last_name: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={editForm.email} onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tél. personnel</Label>
                  <Input value={editForm.phone} onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Tél. professionnel</Label>
                  <Input value={editForm.phone_pro} onChange={(e) => setEditForm(prev => ({ ...prev, phone_pro: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>WhatsApp</Label>
                  <Input value={editForm.whatsapp} onChange={(e) => setEditForm(prev => ({ ...prev, whatsapp: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <Label>Titre / Fonction</Label>
                  <Input value={editForm.job_title} onChange={(e) => setEditForm(prev => ({ ...prev, job_title: e.target.value }))} placeholder="Conseiller Financier" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select value={editForm.status} onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="blocked">Bloqué</SelectItem>
                    <SelectItem value="pending">En attente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Commissions */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Commission Virement (%)</Label>
                  <Input type="number" step="0.1" min="0" max="100" value={editForm.commission_virement} onChange={(e) => setEditForm(prev => ({ ...prev, commission_virement: parseFloat(e.target.value) || 0 }))} data-testid="edit-analyst-commission-virement" />
                </div>
                <div className="space-y-2">
                  <Label>Commission Crypto (%)</Label>
                  <Input type="number" step="0.1" min="0" max="100" value={editForm.commission_crypto} onChange={(e) => setEditForm(prev => ({ ...prev, commission_crypto: parseFloat(e.target.value) || 0 }))} data-testid="edit-analyst-commission-crypto" />
                </div>
              </div>

              {/* Horaires de disponibilité */}
              <div className="space-y-3 pt-2">
                <Label className="text-sm font-semibold">Horaires de disponibilité</Label>
                <div className="space-y-2">
                  {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map(day => {
                    const d = editForm.availability?.[day] || { active: false, start: '', end: '' };
                    return (
                      <div key={day} className="flex items-center gap-3">
                        <button
                          onClick={() => setEditForm(prev => ({
                            ...prev,
                            availability: { ...prev.availability, [day]: { ...d, active: !d.active } }
                          }))}
                          className={`w-5 h-5 rounded border flex-shrink-0 flex items-center justify-center text-xs ${d.active ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground'}`}
                        >{d.active ? '✓' : ''}</button>
                        <span className="w-20 text-sm capitalize">{day}</span>
                        {d.active ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Input
                              type="time"
                              value={d.start}
                              onChange={(e) => setEditForm(prev => ({
                                ...prev,
                                availability: { ...prev.availability, [day]: { ...d, start: e.target.value } }
                              }))}
                              className="h-8 w-24 text-xs"
                            />
                            <span className="text-muted-foreground">→</span>
                            <Input
                              type="time"
                              value={d.end}
                              onChange={(e) => setEditForm(prev => ({
                                ...prev,
                                availability: { ...prev.availability, [day]: { ...d, end: e.target.value } }
                              }))}
                              className="h-8 w-24 text-xs"
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Fermé</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleEditAnalyst} disabled={saving}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Info & Stats Dialog */}
        <Dialog open={infoDialogOpen} onOpenChange={setInfoDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Profil & Statistiques
              </DialogTitle>
            </DialogHeader>
            {selectedAnalyst && (
              <div className="space-y-6 py-2">
                {/* Profile Section */}
                <div className="flex items-start gap-6 p-4 rounded-lg border bg-card">
                  <div className="flex-shrink-0">
                    {selectedAnalyst.profile_photo_url ? (
                      <img src={selectedAnalyst.profile_photo_url} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-primary" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center text-2xl font-bold text-primary">
                        {(selectedAnalyst.first_name?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold">{selectedAnalyst.first_name} {selectedAnalyst.last_name}</h3>
                      <div className="flex items-center gap-2">
                        <Badge className={selectedAnalyst.status === 'active' ? 'bg-green-600' : 'bg-red-600'}>{selectedAnalyst.status === 'active' ? 'Actif' : 'Bloqué'}</Badge>
                        <Button variant="ghost" size="sm" onClick={() => { setInfoDialogOpen(false); setTimeout(() => openEditDialog(selectedAnalyst), 200); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {selectedAnalyst.job_title && <p className="text-sm text-muted-foreground">{selectedAnalyst.job_title}</p>}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground"><Mail className="w-3.5 h-3.5" /> {selectedAnalyst.email}</div>
                      {selectedAnalyst.phone && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> {selectedAnalyst.phone}</div>}
                      {selectedAnalyst.phone_pro && <div className="flex items-center gap-2 text-muted-foreground"><Phone className="w-3.5 h-3.5" /> {selectedAnalyst.phone_pro} (pro)</div>}
                      {selectedAnalyst.whatsapp && <div className="flex items-center gap-2 text-muted-foreground"><MessageSquare className="w-3.5 h-3.5" /> {selectedAnalyst.whatsapp}</div>}
                      <div className="flex items-center gap-2 text-muted-foreground"><Calendar className="w-3.5 h-3.5" /> Créé le {selectedAnalyst.created_at ? new Date(selectedAnalyst.created_at).toLocaleDateString('fr-FR') : '—'}</div>
                      {selectedAnalyst.visible_pin && <div className="flex items-center gap-2 text-muted-foreground"><Lock className="w-3.5 h-3.5" /> PIN: {selectedAnalyst.visible_pin}</div>}
                    </div>
                    {/* Availability */}
                    {selectedAnalyst.availability && (
                      <div className="pt-2">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Disponibilités</p>
                        <div className="flex flex-wrap gap-1">
                          {['lundi','mardi','mercredi','jeudi','vendredi','samedi','dimanche'].map(day => {
                            const d = selectedAnalyst.availability?.[day];
                            if (!d?.active) return <span key={day} className="text-[10px] px-2 py-0.5 rounded bg-muted text-muted-foreground">{day.slice(0,3)}</span>;
                            return <span key={day} className="text-[10px] px-2 py-0.5 rounded bg-primary/20 text-primary font-semibold">{day.slice(0,3)} {d.start}-{d.end}</span>;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats Filter */}
                <div className="flex items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Du</Label>
                    <Input type="date" value={statsStartDate} onChange={(e) => setStatsStartDate(e.target.value)} className="h-8 text-sm w-36" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Au</Label>
                    <Input type="date" value={statsEndDate} onChange={(e) => setStatsEndDate(e.target.value)} className="h-8 text-sm w-36" />
                  </div>
                  <Button size="sm" className="h-8" onClick={() => fetchAnalystStats(selectedAnalyst.id, statsStartDate, statsEndDate)}>
                    Filtrer
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => { setStatsStartDate(''); setStatsEndDate(''); fetchAnalystStats(selectedAnalyst.id); }}>
                    Tout
                  </Button>
                </div>

                {/* Stats Grid */}
                {statsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Chargement...</div>
                ) : analystStats ? (
                  <div className="space-y-4">
                    {/* Key metrics */}
                    <div className="grid grid-cols-4 gap-3">
                      {[
                        { label: 'Clients', value: analystStats.total_clients, icon: Users, color: 'text-blue-500' },
                        { label: 'AUM total', value: `${analystStats.total_aum?.toLocaleString('fr-FR')} €`, icon: Wallet, color: 'text-purple-500' },
                        { label: 'KYC en attente', value: analystStats.kyc_pending, icon: FileText, color: 'text-amber-500' },
                        { label: 'P&L total', value: `${analystStats.trading?.total_pnl >= 0 ? '+' : ''}${analystStats.trading?.total_pnl?.toLocaleString('fr-FR')} €`, icon: TrendingUp, color: analystStats.trading?.total_pnl >= 0 ? 'text-green-500' : 'text-red-500' },
                      ].map(m => (
                        <Card key={m.label} className="bg-card/80">
                          <CardContent className="py-3 px-4 text-center">
                            <m.icon className={`w-5 h-5 mx-auto mb-1 ${m.color}`} />
                            <p className="text-lg font-bold">{m.value}</p>
                            <p className="text-[10px] text-muted-foreground">{m.label}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {/* Transactions */}
                    <Card>
                      <CardContent className="py-3 px-4">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><ArrowUpRight className="w-4 h-4 text-green-500" /> Transactions</h4>
                        <div className="grid grid-cols-5 gap-2 text-center text-xs">
                          <div><p className="text-lg font-bold">{analystStats.transactions?.total}</p><p className="text-muted-foreground">Total</p></div>
                          <div><p className="text-lg font-bold text-amber-500">{analystStats.transactions?.pending}</p><p className="text-muted-foreground">En attente</p></div>
                          <div><p className="text-lg font-bold text-green-500">{analystStats.transactions?.completed}</p><p className="text-muted-foreground">Validées</p></div>
                          <div><p className="text-lg font-bold text-red-500">{analystStats.transactions?.rejected}</p><p className="text-muted-foreground">Refusées</p></div>
                          <div>
                            <p className="text-lg font-bold text-green-500">+{analystStats.transactions?.total_deposits?.toLocaleString('fr-FR')} €</p>
                            <p className="text-muted-foreground">Dépôts</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Trading */}
                    <Card>
                      <CardContent className="py-3 px-4">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-blue-500" /> Trading</h4>
                        <div className="grid grid-cols-5 gap-2 text-center text-xs">
                          <div><p className="text-lg font-bold">{analystStats.trading?.total_positions}</p><p className="text-muted-foreground">Positions</p></div>
                          <div><p className="text-lg font-bold text-blue-500">{analystStats.trading?.open}</p><p className="text-muted-foreground">Ouvertes</p></div>
                          <div><p className="text-lg font-bold text-amber-500">{analystStats.trading?.pending}</p><p className="text-muted-foreground">En attente</p></div>
                          <div><p className="text-lg font-bold text-green-500">{analystStats.trading?.winning}</p><p className="text-muted-foreground">Gagnantes</p></div>
                          <div><p className="text-lg font-bold">{analystStats.trading?.win_rate}%</p><p className="text-muted-foreground">Taux réussite</p></div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Placements & Messages */}
                    <div className="grid grid-cols-2 gap-3">
                      <Card>
                        <CardContent className="py-3 px-4">
                          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><Wallet className="w-4 h-4 text-purple-500" /> Placements</h4>
                          <div className="grid grid-cols-2 gap-2 text-center text-xs">
                            <div><p className="text-lg font-bold">{analystStats.placements?.active}</p><p className="text-muted-foreground">Actifs</p></div>
                            <div><p className="text-lg font-bold text-purple-500">{analystStats.placements?.total_invested?.toLocaleString('fr-FR')} €</p><p className="text-muted-foreground">Investis</p></div>
                          </div>
                        </CardContent>
                      </Card>
                      <Card>
                        <CardContent className="py-3 px-4">
                          <h4 className="text-sm font-semibold mb-2 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cyan-500" /> Messages</h4>
                          <div className="grid grid-cols-2 gap-2 text-center text-xs">
                            <div><p className="text-lg font-bold">{analystStats.messages?.sent}</p><p className="text-muted-foreground">Envoyés</p></div>
                            <div><p className="text-lg font-bold">{analystStats.messages?.received}</p><p className="text-muted-foreground">Reçus</p></div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Paiements & Commissions */}
                    {analystStats.payments && (
                      <Card>
                        <CardContent className="py-3 px-4">
                          <h4 className="text-sm font-semibold mb-3 flex items-center gap-2"><Activity className="w-4 h-4 text-emerald-500" /> Paiements & Commissions</h4>
                          <div className="grid grid-cols-3 gap-3 text-center text-xs mb-3">
                            <div><p className="text-lg font-bold">{analystStats.payments?.total}</p><p className="text-muted-foreground">Total paiements</p></div>
                            <div><p className="text-lg font-bold">{analystStats.payments?.total_amount?.toLocaleString('fr-FR')} €</p><p className="text-muted-foreground">Montant total</p></div>
                            <div><p className="text-lg font-bold text-emerald-400">{analystStats.commissions?.total_expected?.toLocaleString('fr-FR')} €</p><p className="text-muted-foreground">Comm. totales</p></div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs">
                            <div className="p-2 rounded bg-secondary/30">
                              <p className="font-semibold mb-1">Virements</p>
                              <p className="text-muted-foreground">Taux: {analystStats.commissions?.rate_virement || 0}%</p>
                              <p className="text-yellow-400">Comm. à venir: {analystStats.commissions?.pending?.toLocaleString('fr-FR')} €</p>
                              <p className="text-emerald-400">Comm. confirmées: {analystStats.commissions?.confirmed?.toLocaleString('fr-FR')} €</p>
                            </div>
                            <div className="p-2 rounded bg-secondary/30">
                              <p className="font-semibold mb-1">Crypto</p>
                              <p className="text-muted-foreground">Taux: {analystStats.commissions?.rate_crypto || 0}%</p>
                              <p className="text-yellow-400">Comm. à venir: {analystStats.commissions?.pending?.toLocaleString('fr-FR')} €</p>
                              <p className="text-emerald-400">Comm. confirmées: {analystStats.commissions?.confirmed?.toLocaleString('fr-FR')} €</p>
                            </div>
                          </div>
                          {analystStats.payments?.by_status && Object.keys(analystStats.payments.by_status).length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {Object.entries(analystStats.payments.by_status).map(([status, count]) => {
                                const statusLabels = { en_attente: 'En attente', envoye: 'Envoyé', bloque: 'Bloqué', recu: 'Reçu', paye: 'Payé', annule: 'Annulé' };
                                return (
                                  <span key={status} className="text-[10px] px-2 py-1 rounded bg-muted/50">{statusLabels[status] || status}: <strong>{count}</strong></span>
                                );
                              })}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                ) : null}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Supprimer l&apos;analyste
              </DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer {selectedAnalyst?.first_name} {selectedAnalyst?.last_name} ?
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDeleteAnalyst} disabled={saving}>
                {saving ? 'Suppression...' : 'Supprimer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={!!showResetPwdDialog} onOpenChange={() => setShowResetPwdDialog(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
              <DialogDescription>
                {showResetPwdDialog?.first_name} {showResetPwdDialog?.last_name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label>Nouveau mot de passe</Label>
                <Input
                  type="text"
                  value={customPassword}
                  onChange={(e) => setCustomPassword(e.target.value)}
                  placeholder="Laissez vide pour générer automatiquement"
                  className="font-mono"
                  data-testid="reset-pwd-input"
                />
                <p className="text-xs text-muted-foreground">Saisissez un mot de passe ou laissez vide pour en générer un aléatoire</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowResetPwdDialog(null)}>Annuler</Button>
                <Button className="flex-1" onClick={handleResetPassword} data-testid="confirm-reset-pwd-btn">
                  {customPassword ? 'Appliquer' : 'Générer et appliquer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
